import { PDFDocument, rgb, StandardFonts, PDFFont, RGB } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import type { Shipment, PublicShipmentForDrivers } from "../types";
import type { Country } from "../types";

type CountryCode = { _id: string } | string | undefined | null;

async function loadFonts(
  pdfDoc: PDFDocument
): Promise<{ regular: PDFFont; bold: PDFFont }> {
  pdfDoc.registerFontkit(fontkit);
  const fontSources = [
    {
      regular: "/fonts/NotoSans-Regular.ttf",
      bold: "/fonts/NotoSans-Bold.ttf",
    },
  ];
  for (const source of fontSources) {
    try {
      const [regularResponse, boldResponse] = await Promise.all([
        fetch(source.regular),
        fetch(source.bold),
      ]);
      if (regularResponse.ok && boldResponse.ok) {
        const regularBytes = new Uint8Array(
          await regularResponse.arrayBuffer()
        );
        const boldBytes = new Uint8Array(await boldResponse.arrayBuffer());
        const regular = await pdfDoc.embedFont(regularBytes);
        const bold = await pdfDoc.embedFont(boldBytes);
        return { regular, bold };
      }
    } catch (error) {
      console.warn("Font source failed, trying next:", error);
      continue;
    }
  }
  // fallback
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  return { regular, bold };
}

export async function createCmrPdf(
  data: Shipment | PublicShipmentForDrivers,
  countries: Country[]
): Promise<Uint8Array> {
  try {
    const pdfDoc = await PDFDocument.create();

    const countryOptions = (countries ?? []).flat().map((country) => ({
      value: country._id,
      label: country.name,
    }));

    const getCountryName = (codeOrName: CountryCode): string => {
      if (!codeOrName) return "";
      const countryCode =
        typeof codeOrName === "string" ? codeOrName : codeOrName._id;
      const country = countryOptions.find(
        (c) => c.value.toLowerCase() === countryCode.toLowerCase()
      );
      return (country && country.label) || "";
    };

    const pickupCountry = getCountryName(data?.pickup?.address?.country);
    const deliveryCountry = getCountryName(data.delivery?.address?.country);
    const carrierCountry = getCountryName(data?.carrier?.address?.country);

    // Set PDF metadata
    pdfDoc.setTitle(`CMR ${data?.carrier?.companyName || ""}`);
    pdfDoc.setAuthor("CMR Generator");
    pdfDoc.setSubject(
      `CMR Document for Shipment ${(data?.referenceNumber || "-").replace(
        "#",
        ""
      )}`
    );
    pdfDoc.setKeywords(["CMR", "Shipment", "Transport", "Document"]);
    pdfDoc.setProducer("CMR PDF Generator");
    pdfDoc.setCreator("CMR System");
    pdfDoc.setCreationDate(new Date());
    pdfDoc.setModificationDate(new Date());

    const page = pdfDoc.addPage([595, 842]);
    const { height } = page.getSize();

    const { regular: font, bold: boldFont } = await loadFonts(pdfDoc);

    const black = rgb(0, 0, 0);

    const drawBox = (x: number, y: number, width: number, height: number) => {
      page.drawRectangle({
        x,
        y: page.getHeight() - y - height,
        width,
        height,
        borderColor: black,
        borderWidth: 0.5,
      });
    };

    const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
      page.drawLine({
        start: { x: x1, y: page.getHeight() - y1 },
        end: { x: x2, y: page.getHeight() - y2 },
        color: black,
        thickness: 0.5,
      });
    };

    const drawText = (
      text: string,
      x: number,
      y: number,
      options?: {
        size?: number;
        font?: PDFFont;
        color?: RGB;
        maxWidth?: number;
      }
    ) => {
      const opts = {
        size: 10,
        font: font, // Default to regular font
        color: black,
        ...options,
      };

      // Safely handle undefined or null text
      const safeText = text || "";

      if (!safeText) {
        return; // Don't draw empty text
      }

      if (options?.maxWidth && safeText && safeText.length > 50) {
        const words = safeText.split(" ");
        let line = "";
        let yOffset = 0;

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const testLine = line + word + " ";

          try {
            const textWidth = opts.font.widthOfTextAtSize(testLine, opts.size);

            if (textWidth > options.maxWidth && line.length > 0) {
              try {
                page.drawText(line.trim(), {
                  x,
                  y: height - y - yOffset,
                  size: opts.size,
                  font: opts.font,
                  color: opts.color,
                });
              } catch (drawError) {
                console.warn("Error drawing text line, skipping:", drawError);
              }
              line = word + " ";
              yOffset += opts.size + 4;
            } else {
              line = testLine;
            }

            if (i === words.length - 1) {
              try {
                page.drawText(line.trim(), {
                  x,
                  y: height - y - yOffset,
                  size: opts.size,
                  font: opts.font,
                  color: opts.color,
                });
              } catch (drawError) {
                console.warn(
                  "Error drawing final text line, skipping:",
                  drawError
                );
              }
            }
          } catch (textError) {
            console.warn("Error measuring text width:", textError);
            // Fallback: just draw the text without width checking
            try {
              page.drawText(safeText, {
                x,
                y: height - y,
                size: opts.size,
                font: opts.font,
                color: opts.color,
              });
            } catch (fallbackError) {
              console.warn(
                "Fallback text drawing also failed, skipping text:",
                fallbackError
              );
            }
            break;
          }
        }
      } else {
        try {
          page.drawText(safeText, {
            x,
            y: height - y,
            size: opts.size,
            font: opts.font,
            color: opts.color,
          });
        } catch (textError) {
          console.warn("Error drawing text, skipping:", textError);
        }
      }
    };

    let yPos = 30;

    drawBox(40, 20, 515, 760);

    const outerRight = 40 + 515;
    const leftColumnWidth = 250;
    const rightColumnStart = 40 + leftColumnWidth;
    const rightColumnWidth = 265;

    // Calculate position: 30 + 90 + 90 + 60 + 75 + 22.5 = 367.5 (bottom line of Documents Attached)
    drawLine(rightColumnStart, 20, rightColumnStart, 287.5);

    const pickupBottom = yPos + 60;
    drawLine(40, pickupBottom, outerRight, pickupBottom);

    drawText("1.", 55, yPos + 7.5, { size: 10, font: boldFont });
    drawText(
      " Shipper / Consignor / Sender (Name, address, country)",
      68,
      yPos + 7.5,
      { size: 8.5, font: font }
    );
    let contentY = yPos + 22.5;
    drawText(data?.pickup?.companyName?.toUpperCase(), 55, contentY, {
      size: 10,
      font: boldFont,
    });
    contentY += 12;
    drawText(
      data?.pickup?.address?.street?.split(",")[0].toUpperCase(),
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
        maxWidth: 180,
      }
    );
    contentY += 12;
    drawText(
      `${(data.pickup?.address?.state || "").toUpperCase()}${
        data.pickup?.address?.postalCode
          ? ` (${data.pickup.address.postalCode})`
          : ""
      }`,
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    contentY += 12;
    drawText(
      `${data.pickup?.address?.city || ""}, ${pickupCountry}`.toUpperCase(),
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    yPos += 90;

    drawLine(40, yPos + 38, outerRight, yPos + 38);

    drawText("2.", 55, yPos - 15, { size: 10, font: boldFont });
    drawText(" Receiver / Consignee (Name, address, country)", 68, yPos - 15, {
      size: 9,
      font: font,
    });
    contentY = yPos;
    drawText(data.delivery?.companyName.toUpperCase(), 55, contentY, {
      size: 10,
      font: boldFont,
    });
    contentY += 12;
    drawText(
      data.delivery?.address?.street.split(",")[0].toUpperCase(),
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
        maxWidth: 180,
      }
    );
    contentY += 12;
    drawText(
      `${(data.delivery?.address?.state || "").toUpperCase()}${
        data.delivery?.address?.postalCode
          ? ` (${data.delivery.address.postalCode})`
          : ""
      }`,
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    contentY += 12;
    drawText(
      `${data.delivery?.address?.city || ""}, ${deliveryCountry}`.toUpperCase(),
      55,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    yPos += 90;
    drawText("3.", 55, yPos - 40.5, { size: 10, font: boldFont });
    drawText(" Place of delivery (Address, Country)", 68, yPos - 40.5, {
      size: 9,
      font: font,
    });

    let contentYPlaceOfDelivery = yPos - 25.5;
    const street = data?.delivery?.address?.street?.trim() || "";

    // Define a max character limit before wrapping
    const maxCharsPerLine = 40; // adjust based on your page width and font size

    if (street.length > maxCharsPerLine) {
      // Split into words and wrap roughly at the halfway point
      const words = street.split(" ");
      let firstLine = "";
      let secondLine = "";

      for (const word of words) {
        if ((firstLine + " " + word).trim().length <= maxCharsPerLine) {
          firstLine += ` ${word}`;
        } else {
          secondLine += ` ${word}`;
        }
      }

      // Draw both lines
      drawText(firstLine.trim().toUpperCase(), 55, contentYPlaceOfDelivery, {
        size: 10,
        font: boldFont,
      });
      drawText(
        secondLine.trim().toUpperCase(),
        55,
        contentYPlaceOfDelivery + 10,
        {
          size: 10,
          font: boldFont,
        }
      );

      yPos += 70;
    } else {
      drawText(street.toUpperCase(), 55, contentYPlaceOfDelivery, {
        size: 10,
        font: boldFont,
      });

      yPos += 70;
    }

    // Add horizontal line 15 pixels above the titles
    drawLine(40, yPos - 75.5, outerRight, yPos - 75.5);

    drawText("4.", 55, yPos - 60.5, { size: 10, font: boldFont });
    drawText(" Place of pickup (Address, Country)", 68, yPos - 60.5, {
      size: 9,
      font: font,
    });

    // Add 18. Carrier's reservations and observations on the same line
    drawText("18.", rightColumnStart + 10, yPos - 60.5, {
      size: 10,
      font: boldFont,
    });
    drawText(
      " Carrier's reservations and observations",
      rightColumnStart + 23,
      yPos - 60.5,
      { size: 9, font: font }
    );

    // Add horizontal line 10 pixels below the titles
    drawLine(40, yPos - 30.5, outerRight, yPos - 30.5);

    // Add horizontal line to create box for 18. Carrier's observations (25 pixels below previous line)
    // drawLine(rightColumnStart, yPos + 50, outerRight, yPos + 50);

    // Add shipment ID in the box below carrier's reservations
    if (data.referenceNumber) {
      drawText(
        `ID: ${data.referenceNumber}`,
        rightColumnStart + 10,
        yPos - 20.5,
        { size: 9, font: boldFont }
      );
    }

    // Add horizontal line 30 pixels below the ID
    // drawLine(40, yPos + 9.5, outerRight, yPos + 9.5);

    contentY = yPos - 45.5;
    const pickupstreet = data?.pickup?.address?.street?.trim() || "";

    if (pickupstreet.length > maxCharsPerLine) {
      // Split into words and wrap roughly at the halfway point
      const words = pickupstreet.split(" ");
      let firstLine = "";
      let secondLine = "";

      for (const word of words) {
        if ((firstLine + " " + word).trim().length <= maxCharsPerLine) {
          firstLine += ` ${word}`;
        } else {
          secondLine += ` ${word}`;
        }
      }

      // Draw both lines
      drawText(firstLine.trim().toUpperCase(), 55, contentY, {
        size: 10,
        font: boldFont,
      });
      drawText(secondLine.trim().toUpperCase(), 55, contentY + 10, {
        size: 10,
        font: boldFont,
      });

      yPos += 75; // add more space since two lines
    } else {
      // Single-line version
      drawText(pickupstreet.toUpperCase(), 55, contentY, {
        size: 10,
        font: boldFont,
      });

      yPos += 75;
    }

    // Add horizontal line 15 pixels above the title (connects to vertical separator)
    // drawLine(40, yPos - 2.5, rightColumnStart, yPos - 2.5);

    drawText("5.", 55, yPos - 95.5, { size: 10, font: boldFont });
    drawText(" Documents Attached", 68, yPos - 95.5, { size: 9, font: font });

    // Add horizontal line 10 pixels below the title
    // drawLine(40, yPos + 22.5, outerRight, yPos + 22.5);

    yPos += 75;

    yPos = 30;

    drawText(
      "INTERNATIONAL CONSIGNMENT NOTE - CMR",
      rightColumnStart + 10,
      yPos + 7.5,
      { size: 9, font: boldFont }
    );
    contentY = yPos + 22.5;
    const cmrText =
      "This carriage is subject, notwithstanding any clause to the contrary, to the Convention on the Contract of the International Carriage of Goods by Road (CMR)";
    drawText(cmrText, rightColumnStart + 10, contentY, {
      size: 9,
      font: font,
      maxWidth: rightColumnWidth - 20,
    });

    drawText("16.", rightColumnStart + 10, 105, { size: 10, font: boldFont });
    drawText(" Carrier (Name, address, country)", rightColumnStart + 23, 105, {
      size: 9,
      font: font,
    });
    // Position content 15px below the title line
    contentY = 120;
    if (data.carrier?.companyName) {
      drawText(
        data.carrier?.companyName.toUpperCase() || "",
        rightColumnStart + 10,
        contentY,
        {
          size: 10,
          font: boldFont,
        }
      );
    }

    contentY += 12;
    if (data.carrier?.address?.street) {
      drawText(
        data.carrier?.address?.street?.split(",")[0].toUpperCase(),
        rightColumnStart + 10,
        contentY,
        {
          size: 10,
          font: boldFont,
          maxWidth: 180,
        }
      );
    }

    contentY += 12;
    drawText(
      `${(data.carrier?.address?.state || "").toUpperCase()}${
        data.carrier?.address?.postalCode
          ? ` (${data.carrier.address.postalCode})`
          : ""
      }`,
      rightColumnStart + 10,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    contentY += 12;
    drawText(
      `${data.carrier?.address?.city || ""}, ${carrierCountry}`.toUpperCase(),
      rightColumnStart + 10,
      contentY,
      {
        size: 10,
        font: boldFont,
      }
    );
    contentY += 12;

    drawText("17.", rightColumnStart + 10, 169.5, { size: 10, font: boldFont });
    drawText(" Successive Carrier (Name)", rightColumnStart + 23, 169.5, {
      size: 9,
      font: font,
    });
    // Position content 15px below the title line
    if (data.carrier?.companyName) {
      drawText(
        data.carrier?.companyName.toUpperCase(),
        rightColumnStart + 10,
        184.5,
        {
          size: 10,
          font: boldFont,
        }
      );
    }

    yPos = 15 + 90 + 90 + 75 + 75 + 75 - 30;

    // Add horizontal line above sections 6-12
    // drawLine(40, 367.5, outerRight, 367.5);

    // Add horizontal line 30 pixels below section 5 and ID
    drawLine(40, 287.5, outerRight, 287.5);

    const boxHeight = 36;
    const boxStartY = yPos + 5;
    const boxEndY = yPos + boxHeight + 5;

    const box6Width = 75;
    const box7Width = 65;
    const box8Width = 75;
    const box9Width = 80;
    const box10Width = 75;
    const box11Width = 75;
    // const box12Width = 75;

    let currentX = 40;

    drawText("6.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Marks and", currentX + 18, 302.5, { size: 8, font: font });
    drawText("numbers", currentX + 20, 310.5, { size: 8, font: font });
    const cargoItems = (data?.cargoItems || []).slice(0, 5);

    // Define starting Y and spacing between each mark
    let startYCargoItems = 332.5;
    const spacing = 24; // Adjust as needed

    cargoItems.forEach((item, index) => {
      if (item?.marks) {
        drawText(
          item.marks.toUpperCase(),
          currentX + 5,
          startYCargoItems + index * spacing,
          {
            size: 8,
            font: boldFont,
          }
        );
      }
    });
    drawLine(
      currentX + box6Width + 10,
      287.5,
      currentX + box6Width + 10,
      boxStartY + 40
    );
    currentX += box6Width;

    drawText("7.", currentX + 12, 302.5, { size: 9, font: boldFont });
    drawText(" No. of", currentX + 22, 302.5, { size: 8, font: font });
    drawText("packages", currentX + 24, 310.5, { size: 8, font: font });
    cargoItems.forEach((item, index) => {
      if (item?.quantity) {
        drawText(
          `${item?.quantity}`,
          currentX + 14,
          startYCargoItems + index * spacing,
          {
            size: 8,
            font: boldFont,
          }
        );
      }
    });
    drawLine(currentX + box7Width, 287.5, currentX + box7Width, boxStartY + 40);
    currentX += box7Width;

    drawText("8.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Method of", currentX + 18, 302.5, { size: 8, font: font });
    drawText("packing", currentX + 20, 310.5, { size: 8, font: font });
    cargoItems.forEach((item, index) => {
      if (item?.type) {
        drawText(
          item?.type.toUpperCase(),
          currentX + 10,
          startYCargoItems + index * spacing,
          {
            size: 8,
            font: boldFont,
          }
        );
      }
    });
    drawLine(currentX + box8Width, 287.5, currentX + box8Width, boxStartY + 40);
    currentX += box8Width;

    drawText("9.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Nature of", currentX + 18, 302.5, { size: 8, font: font });
    drawText("Goods", currentX + 20, 310.5, { size: 8, font: font });
    cargoItems.forEach((item, index) => {
      const desc = item?.description?.trim();
      if (!desc) return; // skip empty or missing descriptions

      const y = startYCargoItems + index * spacing;

      if (desc.length > 12) {
        const words = desc.split(" ");
        const midPoint = Math.ceil(words.length / 2);
        const firstLine = words.slice(0, midPoint).join(" ");
        const secondLine = words.slice(midPoint).join(" ");

        drawText(firstLine.toUpperCase(), currentX + 5, y, {
          size: 8,
          font: boldFont,
        });
        drawText(secondLine.toUpperCase(), currentX + 5, y + 8, {
          size: 8,
          font: boldFont,
        });
      } else {
        drawText(desc.toUpperCase(), currentX + 5, y, {
          size: 8,
          font: boldFont,
        });
      }
    });
    drawLine(currentX + box9Width, 287.5, currentX + box9Width, boxStartY + 40);
    currentX += box9Width;

    drawText("10.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Statistical", currentX + 23, 302.5, { size: 8, font: font });
    drawText("number", currentX + 25, 310.5, { size: 8, font: font });
    drawLine(
      currentX + box10Width,
      287.5,
      currentX + box10Width,
      boxStartY + 40
    );
    currentX += box10Width;

    drawText("11.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Gross", currentX + 23, 302.5, { size: 8, font: font });
    drawText("weight (kg)", currentX + 25, 310.5, { size: 8, font: font });
    cargoItems.forEach((item, index) => {
      if (item?.weightKg) {
        drawText(
          `${item?.weightKg}`,
          currentX + 10,
          startYCargoItems + index * spacing,
          {
            size: 8,
            font: boldFont,
          }
        );
      }
    });
    drawLine(
      currentX + box11Width,
      287.5,
      currentX + box11Width,
      boxStartY + 40
    );
    currentX += box11Width;

    drawText("12.", currentX + 5, 302.5, { size: 9, font: boldFont });
    drawText(" Volume (m³)", currentX + 20, 302.5, { size: 8, font: font });
    cargoItems.forEach((item, index) => {
      if (item?.volumeM3) {
        drawText(
          `${item?.volumeM3}`,
          currentX + 10,
          startYCargoItems + index * spacing,
          {
            size: 8,
            font: boldFont,
          }
        );
      }
    });

    // Add horizontal line below the titles of 6-12
    drawLine(40, 317.5, outerRight, 317.5);

    // Add horizontal lines below the values of 6-12 for 5 rows of goods
    // First line 15 pixels below values (at 332.5 + 15 = 347.5)
    drawLine(40, 347.5, outerRight, 347.5);
    // Additional lines for 4 more rows (22px spacing between rows)
    drawLine(40, 371.5, outerRight, 371.5);
    drawLine(40, 393.5, outerRight, 393.5);
    drawLine(40, 415.5, outerRight, 415.5);
    drawLine(40, 435, outerRight, 435);

    yPos = boxEndY + 30;

    // Add vertical divider aligned with the column divider above
    drawLine(rightColumnStart, boxStartY + 40, rightColumnStart, yPos);

    // Add 13. Sender's instructions in the left section
    const sectionMiddleY = boxStartY + 55; // 15 pixels below the bottom line of 6-12 values
    drawText("13.", 60, sectionMiddleY - 4, {
      size: 10,
      font: boldFont,
    });
    drawText(" Sender's instructions", 73, sectionMiddleY - 4, {
      size: 9,
      font: font,
    });
    drawText(
      "(treatment of carriage in custom, etc.)",
      60,
      sectionMiddleY + 6,
      {
        size: 9,
        font: font,
      }
    );

    // Add full width line at the bottom of the title
    drawLine(40, sectionMiddleY + 11, outerRight, sectionMiddleY + 11);

    // Add horizontal lines to create a box below Sender's instructions
    const boxBottom = yPos + 65; // 65px below the bottom horizontal line
    const boxTop = boxBottom - 15; // 15px above the bottom line
    drawLine(40, boxTop, rightColumnStart, boxTop);

    // Add "declared price of goods" title in the thin section
    const declaredPriceY = (boxTop + boxBottom) / 2 + 3; // Center between boxTop and boxBottom, moved down 3px
    drawText("Declared price of goods", 60, declaredPriceY, {
      size: 8,
      font: font,
    });

    // Add another line 15px below the bottom line, spanning full width
    const thirdLine = boxBottom + 15;

    // Add another line 30px below thirdLine
    const fourthLine = thirdLine + 30;

    // Add "14. Return" title moved up 15px from fourthLine
    const returnTitleY = thirdLine + 15; // Moved up 15px from fourthLine position
    drawText("14.", 60, returnTitleY, {
      size: 10,
      font: boldFont,
    });
    drawText(" Return", 73, returnTitleY, {
      size: 9,
      font: font,
    });

    // Add full width line at the bottom of 14. Return title
    drawLine(40, returnTitleY + 10, outerRight, returnTitleY + 10);

    // Add another line below fourthLine

    // Add titles between fourthLine and fifthLine
    const instructionsTitleY = fourthLine + 6; // Middle between fourthLine and fifthLine, moved up 14px

    // Add vertical divider between sections 15 and 20 (connected from title 14 to bottom of value boxes)
    drawLine(
      rightColumnStart,
      returnTitleY + 10,
      rightColumnStart,
      instructionsTitleY + 35
    );
    drawText("15.", 60, instructionsTitleY, {
      size: 10,
      font: boldFont,
    });
    drawText(
      " Instructions as to payment of carriage",
      73,
      instructionsTitleY,
      {
        size: 9,
        font: font,
      }
    );

    drawText("20.", rightColumnStart + 10, instructionsTitleY, {
      size: 10,
      font: boldFont,
    });
    drawText(" Other agreements", rightColumnStart + 23, instructionsTitleY, {
      size: 9,
      font: font,
    });

    // Add full width horizontal line below the titles 15 and 20 (5 pixels below)
    // drawLine(40, instructionsTitleY + 5, outerRight, instructionsTitleY + 5);

    // Add another horizontal line to create value boxes for 15 and 20 (25 pixels below the first line)
    drawLine(40, instructionsTitleY + 35, outerRight, instructionsTitleY + 35);

    // Add carriage paid and forward text inside section 15 box (stacked)
    drawText("Carriage paid:", 60, instructionsTitleY + 15, {
      size: 8,
      font: font,
    });
    drawText("Carriage forward:", 60, instructionsTitleY + 30, {
      size: 8,
      font: font,
    });

    // Add vertical line to create right section (200px wide)
    const rightSectionX = outerRight - 200;

    // Add title 21. Established In between the lines
    drawText("21.", 60, instructionsTitleY + 50, {
      size: 10,
      font: boldFont,
    });
    drawText(" Established In:", 73, instructionsTitleY + 50, {
      size: 9,
      font: font,
    });
    // Add delivery city and country
    if (data.delivery.address.street) {
      drawText(
        `${data.delivery.address.city} ${deliveryCountry}`.toUpperCase(),
        135,
        instructionsTitleY + 50,
        {
          size: 9,
          font: boldFont,
        }
      );
    }

    drawText("On:", rightSectionX - 100, instructionsTitleY + 50, {
      size: 9,
      font: font,
    });
    // Add delivery date and time
    // Add delivery date and time
    if (data.delivery?.date) {
      // Convert date from YYYY-MM-DD to DD.MM.YY format
      const dateParts = data.delivery.date.split("T")[0].split("-");
      const formattedDate = `${dateParts[2]}.${
        dateParts[1]
      }.${dateParts[0].slice(-2)}`;
      const deliveryDateTime = data.delivery.time
        ? `${formattedDate} / ${data.delivery.time}`
        : formattedDate;
      drawText(deliveryDateTime, rightSectionX - 85, instructionsTitleY + 50, {
        size: 9,
        font: boldFont,
      });
    }

    // Add another horizontal line 25 pixels below the last line (stops at vertical line)
    drawLine(
      40,
      instructionsTitleY + 60,
      rightSectionX + 10,
      instructionsTitleY + 60
    );

    // Add section 26 below section 21, on the same vertical line
    drawText("26.", 60, instructionsTitleY + 75, {
      size: 10,
      font: boldFont,
    });

    // Calculate horizontal spacing for three titles in section 26
    const section26StartX = 73; // Start after the number 26.
    const section26Width = rightSectionX - section26StartX - 20;
    const col26Width = section26Width / 3;
    const col26_1X = section26StartX;
    const col26_2X = col26_1X + col26Width;
    const col26_3X = col26_2X + col26Width;

    // Draw titles side by side on the same line as 26.
    drawText("Truck:", col26_1X + 10, instructionsTitleY + 75, {
      size: 9,
      font: font,
    });

    drawText("Model:", col26_2X, instructionsTitleY + 75, {
      size: 9,
      font: font,
    });

    drawText("Semitrailer:", col26_3X, instructionsTitleY + 75, {
      size: 9,
      font: font,
    });

    // Add horizontal line below section 26 titles, connecting to the line before section 25
    drawLine(
      40,
      instructionsTitleY + 100,
      rightSectionX + 10,
      instructionsTitleY + 100
    );

    // Add horizontal line below the "21. established in" line

    drawText("22.", 60, instructionsTitleY + 115, {
      size: 10,
      font: boldFont,
    });

    drawText("Pickup Time:", 78, instructionsTitleY + 115, {
      size: 9,
      font: font,
    });
    if (data.pickup?.date && data.pickup.time) {
      const dateParts = data.pickup.date.split("T")[0].split("-");
      const formattedDate = `${dateParts[2]}.${
        dateParts[1]
      }.${dateParts[0].slice(-2)}`;
      const pickupDateTime = `${formattedDate} / ${data.pickup.time}`;
      drawText(pickupDateTime, 133, instructionsTitleY + 115, {
        size: 9,
        font: boldFont,
      });
    }

    drawText("Leaving", 60, instructionsTitleY + 130, {
      size: 9,
      font: font,
    });

    drawText("Signature and Stamp of Sender", 60, 770, {
      size: 9,
      font: font,
    });

    // Add vertical separator before section 23
    const section22RightX = rightColumnStart - 10;
    const section23StartX = section22RightX - 60;
    drawLine(section23StartX, instructionsTitleY + 100, section23StartX, 780);

    // Add section 23 with number at top and title at bottom
    drawText("23.", section23StartX + 10, instructionsTitleY + 115, {
      size: 10,
      font: boldFont,
    });
    drawText("Signature, Stamp of Carrier", section23StartX + 10, 770, {
      size: 9,
      font: font,
    });

    // Position for section 24 (no vertical separator)
    // const section23RightX = section22RightX + 120;

    // Add section 24 positioned 10 pixels from the vertical line to its left
    const section24StartX = rightSectionX + 20; // 10 pixels from the vertical line at rightSectionX + 10

    drawText("24.", section24StartX, instructionsTitleY + 115, {
      size: 10,
      font: boldFont,
    });

    drawText(
      "Date of goods received:",
      section24StartX + 23,
      instructionsTitleY + 115,
      {
        size: 9,
        font: font,
      }
    );

    drawText("Arriving:", section24StartX + 23, instructionsTitleY + 130, {
      size: 9,
      font: font,
    });

    drawText("Leaving:", section24StartX + 90, instructionsTitleY + 130, {
      size: 9,
      font: font,
    });

    drawText("Signature and Stamp of Consignee", section24StartX + 23, 770, {
      size: 9,
      font: font,
    });

    drawLine(
      rightSectionX + 10,
      instructionsTitleY + 35,
      rightSectionX + 10,
      780
    );

    // Section 25 in the bottom right section
    drawText("25.", rightSectionX + 20, instructionsTitleY + 50, {
      size: 10,
      font: boldFont,
    });

    // Calculate horizontal spacing for three columns in section 25
    const section25StartX = rightSectionX + 50; // Start after the number 25.
    const section25Width = outerRight - section25StartX - 20; // Total width available with margin
    const columnWidth = section25Width / 2.5; // Increase spacing between columns
    const col1X = section25StartX;
    const col2X = col1X + columnWidth;
    const col3X = col2X + columnWidth;

    // Draw titles side by side on the same line as 25.
    drawText("Truck", col1X - 10, instructionsTitleY + 50, {
      size: 9,
      font: font,
    });

    drawText("Trailer", col2X - 10, instructionsTitleY + 50, {
      size: 9,
      font: font,
    });

    drawText("Container", col3X - 10, instructionsTitleY + 50, {
      size: 9,
      font: font,
    });

    if (data?.carrier?.truckLicensePlate) {
      const licensePlate =
        typeof data.carrier.truckLicensePlate === "object"
          ? data.carrier.truckLicensePlate.registrationNumber
          : data.carrier.truckLicensePlate;

      drawText(
        licensePlate?.toUpperCase() ?? "",
        col1X - 10,
        instructionsTitleY + 65,
        {
          size: 9,
          font: boldFont,
        }
      );
    }

    if (data?.carrier?.trailerLicensePlate) {
      const licensePlate =
        typeof data.carrier.trailerLicensePlate === "object"
          ? data.carrier.trailerLicensePlate.registrationNumber
          : data.carrier.trailerLicensePlate;

      drawText(
        licensePlate?.toUpperCase() ?? "",
        col2X - 10,
        instructionsTitleY + 65,
        {
          size: 9,
          font: boldFont,
        }
      );
    }

    if (data?.carrier?.containerNumber) {
      drawText(
        data.carrier.containerNumber.toUpperCase(),
        col3X - 10,
        instructionsTitleY + 65,
        {
          size: 9,
          font: boldFont,
        }
      );
    }

    // Add horizontal line below section 25
    drawLine(
      rightSectionX + 10,
      instructionsTitleY + 100,
      outerRight,
      instructionsTitleY + 100
    );

    // Add vertical line closer to "To be paid by"
    const paidByLineX = rightColumnStart + 85; // Closer to the text
    drawLine(paidByLineX, boxStartY + 40, paidByLineX, yPos);

    // Add vertical line extending down to the full-width horizontal line
    drawLine(paidByLineX, yPos, paidByLineX, thirdLine);

    // Add vertical line aligned with the right edge of section 13
    drawLine(rightColumnStart, yPos, rightColumnStart, thirdLine);

    // Add 19. To be paid by in the right section
    drawText("19.", rightColumnStart + 6, sectionMiddleY + 1, {
      size: 10,
      font: boldFont,
    });
    drawText(" To be paid by", rightColumnStart + 19, sectionMiddleY + 1, {
      size: 9,
      font: font,
    });

    // Add horizontal line below "To be paid by" title
    const paidByLineBelow = sectionMiddleY + 35;
    drawLine(rightColumnStart, paidByLineBelow, paidByLineX, paidByLineBelow);

    // Add another line aligned with the second line on the left (boxBottom)
    drawLine(rightColumnStart, boxBottom, outerRight, boxBottom);

    // Add "Carriage charges" in the box above (10 pixels below section 13 title)
    const carriageChargesY = sectionMiddleY + 26; // 10 pixels below the bottom line of section 13 title
    drawText("Carriage charges", rightColumnStart + 10, carriageChargesY, {
      size: 8,
      font: font,
    });

    // Add 4 titles in the section between the two lines
    const sectionHeight = boxBottom - paidByLineBelow;
    const titleSpacing = sectionHeight / 4.5; // Slightly larger spacing
    const startY = paidByLineBelow + titleSpacing * 0.3 + 3; // Start closer to top line, moved down 3px

    drawText("Deduction", rightColumnStart + 10, startY + 3, {
      size: 8,
      font: font,
    });

    drawText("Balance", rightColumnStart + 10, startY + titleSpacing + 3, {
      size: 8,
      font: font,
    });

    drawText(
      "Supplem charges",
      rightColumnStart + 10,
      startY + titleSpacing * 2 + 3,
      {
        size: 8,
        font: font,
      }
    );

    drawText("Other", rightColumnStart + 10, startY + titleSpacing * 3.2 + 2, {
      size: 8,
      font: font,
    });

    // Declare line positions first
    const senderRightLineX = paidByLineX + 55; // 440
    const currencyRightLineX = 485; // Align with line to the right of box 11 (Gross weight)

    // Add "Total" in the box below the four titles
    const totalY = (boxBottom + thirdLine) / 2 + 2; // Center between boxBottom and thirdLine, moved down 2px
    drawText("Total", rightColumnStart + 10, totalY, {
      size: 8,
      font: font,
    });

    // Add vertical lines in the "Total" box aligned with sender, currency, and consignee lines
    drawLine(paidByLineX, boxBottom, paidByLineX, thirdLine);
    drawLine(senderRightLineX, boxBottom, senderRightLineX, thirdLine);
    drawLine(currencyRightLineX, boxBottom, currencyRightLineX, thirdLine);

    // Add full width line below the "Total" (connected with vertical separators)
    drawLine(40, thirdLine, outerRight, thirdLine);

    // Add "Sender" text
    drawText("Sender", paidByLineX + 10, sectionMiddleY + 1, {
      size: 9,
      font: font,
    });

    // Add vertical line to the right of Sender
    drawLine(senderRightLineX, boxStartY + 40, senderRightLineX, yPos);

    // Add "Currency" text
    drawText("Currency", senderRightLineX + 5, sectionMiddleY + 1, {
      size: 9,
      font: font,
    });

    // Add vertical line to the right of Currency
    drawLine(currencyRightLineX, boxStartY + 40, currencyRightLineX, yPos);

    // Add "Consignee" text in the last box
    drawText("Consignee", currencyRightLineX + 10, sectionMiddleY + 1, {
      size: 9,
      font: font,
    });

    yPos += 20;

    // Add CMR watermark in the center of the page
    const centerX = page.getWidth() / 2;
    const centerY = page.getHeight() / 2;

    // Draw oval background with light opacity
    page.drawEllipse({
      x: centerX,
      y: centerY,
      xScale: 120, // Horizontal radius
      yScale: 60, // Vertical radius
      borderColor: rgb(0.6, 0.6, 0.6), // Medium gray border (same as text)
      borderWidth: 8,
      color: rgb(0.95, 0.95, 0.95), // Very light gray fill
      opacity: 0.4, // Same opacity as the text
    });

    // Draw "CMR" text in the center
    page.drawText("CMR", {
      x: centerX - 70, // Offset to center the text (adjusted for larger size)
      y: centerY - 20, // Offset to center the text vertically (adjusted for larger size)
      size: 64,
      font: boldFont,
      color: rgb(0.6, 0.6, 0.6), // Medium gray text
      opacity: 0.4, // Light enough to see through
    });

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (error) {
    console.error("Error creating CMR PDF:", error);
    throw new Error(
      `Failed to create CMR PDF: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

export const downloadCMRPDF = async (
  shipmentData: Shipment,
  countries: Country[],
  filename: string = "CMR.pdf"
) => {
  try {
    const pdfBytes = await createCmrPdf(shipmentData, countries);
    const safeBytes = new Uint8Array(pdfBytes);
    const blob = new Blob([safeBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      filename ||
      `CMR_${shipmentData._id}_${new Date().toISOString().split("T")[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Error downloading CMR PDF:", error);
    throw error;
  }
};
